import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  FileText, X, Download, Loader2, BarChart3, Flag,
} from 'lucide-react';
import { JURISDICTION_NAMES } from '../../lib/constants';
import { JURISDICTION_FLAGS, formatDate, getScoreColor } from './adminUtils';

export const AssessmentDetailModal = ({ assessment, onClose, loading, onDownloadPDF }) => {
  if (!assessment) return null;
  const overall = assessment.scores?.overall || {};
  const overallPct = overall.percentage || 0;
  const isComplete = assessment.status === 'completed';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="assessment-detail-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#3B82F6]" />
            Assessment Details
          </h2>
          <div className="flex items-center gap-2">
            {isComplete && (
              <Button
                variant="outline"
                size="sm"
                data-testid="modal-download-pdf-btn"
                onClick={() => onDownloadPDF(assessment.id, assessment.user?.company_name, assessment.jurisdiction)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-assessment-detail">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 bg-slate-700/30 rounded-lg p-4">
              <span className="text-4xl">{JURISDICTION_FLAGS[assessment.jurisdiction]}</span>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg">{JURISDICTION_NAMES[assessment.jurisdiction]} Assessment</h3>
                <p className="text-slate-400">
                  {assessment.user?.company_name} • {assessment.assessment_type === 'foreign' ? 'Foreign' : 'Domestic'} Company
                </p>
              </div>
              {isComplete && (
                <div className="text-center">
                  <div className={`text-4xl font-bold ${
                    overallPct >= 80 ? 'text-[#10B981]'
                      : overallPct >= 50 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                  }`}
                  >
                    {overallPct}%
                  </div>
                  <Badge className={getScoreColor(overall.level)}>
                    {overall.level?.toUpperCase()} Compliance
                  </Badge>
                </div>
              )}
            </div>

            {/* Company Info */}
            <div className="grid grid-cols-3 gap-4">
              <Info label="Company" value={assessment.user?.company_name} />
              <Info label="Contact" value={assessment.user?.person_name} />
              <Info label="Email" value={assessment.user?.email} valueClass="text-[#3B82F6]" />
            </div>

            {/* Score Breakdown */}
            {isComplete && assessment.scores && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-[#3B82F6]" />
                  Score Breakdown by Category
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(assessment.scores)
                    .filter(([key]) => key !== 'overall' && key !== 'applicability')
                    .map(([category, data]) => (
                      <div key={category} className="bg-slate-700/30 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-slate-300 text-sm capitalize">
                            {category.replace(/_/g, ' ')}
                          </span>
                          <span className={`font-semibold ${
                            data.percentage >= 80 ? 'text-[#10B981]'
                              : data.percentage >= 50 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                          }`}
                          >
                            {data.percentage}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              data.percentage >= 80 ? 'bg-[#10B981]'
                                : data.percentage >= 50 ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'
                            }`}
                            style={{ width: `${data.percentage}%` }}
                          />
                        </div>
                        <p className="text-slate-500 text-xs mt-1">{data.earned}/{data.max} points</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Action Plan */}
            {isComplete && assessment.action_plan?.length > 0 && (
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Flag className="h-4 w-4 text-[#3B82F6]" />
                  Action Plan ({assessment.action_plan.length} items)
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {assessment.action_plan.slice(0, 10).map((item) => (
                    <div key={`${item.category}-${item.action}`} className="bg-slate-700/30 rounded-lg p-3 flex items-start gap-3">
                      <Badge className={
                        item.priority === 'HIGH' ? 'bg-red-500/10 text-red-400'
                          : item.priority === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400'
                            : 'bg-slate-500/10 text-slate-400'
                      }
                      >
                        {item.priority}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-white text-sm">{item.action}</p>
                        <p className="text-slate-500 text-xs mt-1">
                          {item.category?.replace(/_/g, ' ')} • {item.timeline}
                        </p>
                      </div>
                    </div>
                  ))}
                  {assessment.action_plan.length > 10 && (
                    <p className="text-slate-500 text-sm text-center py-2">
                      + {assessment.action_plan.length - 10} more items in PDF report
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="flex gap-4 text-sm">
              <div className="text-slate-400">
                <span className="text-slate-500">Started:</span> {formatDate(assessment.created_at)}
              </div>
              {assessment.completed_at && (
                <div className="text-slate-400">
                  <span className="text-slate-500">Completed:</span> {formatDate(assessment.completed_at)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value, valueClass }) => (
  <div className="bg-slate-700/30 rounded-lg p-3">
    <p className="text-slate-400 text-xs">{label}</p>
    <p className={valueClass || 'text-white font-medium'}>{value}</p>
  </div>
);
